import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { AUTH_API } from "../config/api";
import {
  Container,
  Header,
  Form,
  FormField,
  Input,
  Button,
  SpaceBetween,
  Link,
  Alert,
  Box,
} from "@cloudscape-design/components";
import "./SignUp.css";

const SignUp = () => {
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [invitationToken, setInvitationToken] = useState("");
  const [error, setError] = useState("");
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loading } = useAuth();

  // Read invitation token from URL query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invitation");
    if (token) {
      setInvitationToken(token);
    }
  }, [location.search]);

  // Redirect if user is already logged in
  if (user) {
    navigate("/");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username || !email || !password || !confirmPassword || !firstName || !lastName) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError("Le mot de passe doit contenir au moins une lettre et un chiffre.");
      return;
    }

    try {
      const payload = { username, email, password, first_name: firstName, last_name: lastName };
      if (invitationToken) {
        payload.invitation_token = invitationToken;
      }

      const response = await fetch(AUTH_API.signup, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.email_verification_required) {
          setSignupEmail(email);
          setSignupComplete(true);
          return;
        }
        await login(data);
        if (data.adexcloud_onboarding_pending) {
          navigate("/profile");
        } else {
          navigate("/");
        }
      } else {
        setError(data.error || "Erreur lors de l'inscription. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Signup error:", error);
      setError("Erreur de connexion au serveur. Veuillez réessayer plus tard.");
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMessage("");
    try {
      const response = await fetch(AUTH_API.resendVerification, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: signupEmail }),
      });
      const data = await response.json();
      if (response.ok) {
        setResendMessage("Email de vérification renvoyé. Vérifiez votre boîte de réception.");
      } else {
        setResendMessage(data.error || "Erreur lors de l'envoi de l'email.");
      }
    } catch (error) {
      console.error("Resend verification error:", error);
      setResendMessage("Erreur de connexion au serveur. Veuillez réessayer plus tard.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-bg-image" aria-hidden="true">
        <img src={`${process.env.PUBLIC_URL}/backgroundhero.png`} alt="" />
      </div>
      <div className="signup-overlay" aria-hidden="true" />

      <div className="signup-header">
        <img
          src="/logoadexcloud.png"
          alt="Adexgénie"
          className="signup-logo"
        />
        <div className="signup-title">Adexgénie</div>
        <div className="signup-subtitle">
          Build, deploy and manage AI agents that handle calls, chats, appointments and knowledge bases for your business.
        </div>
      </div>

      <div className="signup-form-overlay">
        <div className="signup-form-card">
          {signupComplete ? (
            <Container
              header={
                <Header variant="h1">
                  Vérifiez votre email
                  <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#5f6b7a', display: 'block', marginTop: '4px' }}>
                    Un email de confirmation a été envoyé
                  </span>
                </Header>
              }
            >
              <SpaceBetween size="l" direction="vertical">
                <Alert type="success">
                  Votre compte a été créé. Un email de vérification a été envoyé à{" "}
                  <strong>{signupEmail}</strong>. Cliquez sur le lien dans l'email pour activer
                  votre compte, puis connectez-vous.
                </Alert>
                {resendMessage && <Alert type={resendMessage.includes("Erreur") ? "error" : "info"}>{resendMessage}</Alert>}
                <SpaceBetween size="s" direction="vertical">
                  <Button
                    variant="primary"
                    fullWidth
                    loading={resendLoading}
                    onClick={handleResend}
                  >
                    Renvoyer l'email de vérification
                  </Button>
                  <Box textAlign="center">
                    <Link href="/signin">Aller à la connexion</Link>
                  </Box>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          ) : (
          <Container
            header={
              <Header variant="h1">
                Sign up
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#5f6b7a', display: 'block', marginTop: '4px' }}>
                  Create your Adexgénie account
                </span>
              </Header>
            }
          >
            <SpaceBetween size="l" direction="vertical">
              {error && <Alert type="error">{error}</Alert>}
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <Form
                  actions={
                    <SpaceBetween direction="vertical" size="s">
                      <Button
                        formAction="submit"
                        variant="primary"
                        loading={loading}
                        fullWidth
                      >
                        Create account
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <SpaceBetween size="l">
                    {invitationToken && (
                      <FormField
                        label="Invitation token"
                        description="Invitation-only registration"
                      >
                        <Input
                          type="text"
                          value={invitationToken}
                          readOnly
                          disabled
                        />
                      </FormField>
                    )}
                    <FormField
                      label="Username"
                      description="Choose a unique username"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.detail.value)}
                        placeholder="Enter your username"
                        disabled={loading}
                      />
                    </FormField>
                    <FormField
                      label="First name"
                      description="Your first name"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.detail.value)}
                        placeholder="Enter your first name"
                        disabled={loading}
                      />
                    </FormField>
                    <FormField
                      label="Last name"
                      description="Your last name"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.detail.value)}
                        placeholder="Enter your last name"
                        disabled={loading}
                        />
                    </FormField>
                    <FormField
                      label="Email"
                      description="Your email address"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.detail.value)}
                        placeholder="Enter your email"
                        disabled={loading}
                      />
                    </FormField>
                    <FormField
                      label="Password"
                      description="At least 8 characters, one letter and one number"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.detail.value)}
                        placeholder="Enter your password"
                        disabled={loading}
                      />
                    </FormField>
                    <FormField
                      label="Confirm password"
                      description="Re-enter your password"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.detail.value)}
                        placeholder="Confirm your password"
                        disabled={loading}
                      />
                    </FormField>
                  </SpaceBetween>
                </Form>
              </form>
              <SpaceBetween size="s" direction="vertical">
                <Box textAlign="center">
                  <Link href="/signin">Already have an account? Sign in</Link>
                </Box>
              </SpaceBetween>
            </SpaceBetween>
          </Container>
          )}
        </div>
      </div>

    </div>
  );
};

export default SignUp;
